import type { PlanDetailsCopy } from '../planDetails'

const ja: PlanDetailsCopy = {
  fieldLabels: {
    current_age: 'ご年齢',
    retirement_age: '退職時期',
    annual_income: '年収',
    annual_savings: '年間貯蓄額',
    inflation: 'インフレ率',
    realization_tax_rate: '譲渡益課税の税率',
    cost_basis: '取得費',
    w0: '現在の投資可能資産',
    primary_residence: 'ご自宅',
    total_investable: '投資可能資産',
    expected_return: '期待リターン',
    volatility: 'ボラティリティ',
    horizon: '計画期間',
    jurisdiction: '税務上の居住地',
    risk_aversion: 'リスク許容度',
    birth_year: '生年',
    birth_date: '生年月日',
    marital_status: '婚姻状況',
    dependents: '扶養家族',
    dependent_ages: '扶養家族の年齢',
    children: 'お子さま',
    children_birth_years: 'お子さまの生年',
    partner_age: '配偶者の年齢',
    partner_annual_income: '配偶者の年収',
    partner_retirement_age: '配偶者の退職年齢',
    plan_to_age: '計画の終了年齢',
    gross_annual_income: '税引前年収',
    net_income: '手取り収入',
    annual_spending: '年間支出',
    essential_spending: '必需支出',
    flexible_spending: '裁量支出',
    investments_total: '投資総額',
    total_investable_wealth: '投資可能資産の合計',
    portfolio_mix: 'ポートフォリオ配分',
    cgt_rate: '金融所得課税の税率',
    interest_rate: '金利',
    dividend_yield: '配当利回り',
    wealth_tax_rate: '富裕税の税率',
    filing_jurisdictions: '申告を行う国',
    citizenship: '国籍',
    tax_residency: '申告上の税務居住地',
    state: '州・県',
    region: '地域',
    city: '市区',
    locality: '市区町村',
    church_tax_applicable: '教会税の課税対象',
    life_cover: '死亡保障額',
    income_protection: '所得補償保険',
    employer_match: '事業主拠出（マッチング）',
    pension: '年金',
    state_pension: '公的年金',
    social_security: '社会保障',
    wage_growth: '賃金上昇率',
    stability: '収入の安定性',
    has_will: '遺言書の有無',
    risk_attitude: 'リスクに対する姿勢',
  },
  keyLabels: {
    goal_priority: '目標の優先順位',
    current_age: '年齢',
    inflation: 'インフレの前提',
    jurisdiction: '税務上の居住地',
    realization_tax_rate: '目標資金のための売却時の税金',
    citizenship: '国籍',
    total_investable: '投資可能資産',
    annual_income: '年収',
    annual_savings: '年間貯蓄額',
    risk_tier: 'リスク水準',
    retirement_age: '退職年齢',
    target_wealth: '目標資産額',
    primary_residence_sellable: '自宅の売却／住み替え',
  },
  channelLabels: {
    web_levers: 'ウェブ上の調整項目',
    web_cockpit: '意思決定パネル',
    web: 'ウェブサイト',
    chat: 'AI アシスタント',
    mcp: 'AI アシスタント',
    full_review: 'フルレビュー',
    full_plan: 'フルプラン',
    what_if: 'シナリオ試算',
  },
  objectiveLabels: {
    status_quo: '現状維持',
    max_goal_probability: '達成確率を最大化',
    lowest_drawdown: '最大ドローダウンを最小化',
    highest_growth: '成長を最大化',
    fastest_to_goal: '目標を最短で達成',
  },
  choices: {
    interest_only: '利息のみ返済',
    fixed_rate_amortizing: '元利返済（固定金利）',
    variable_rate_amortizing: '元利返済（変動金利）',
    repayment: '元利返済',
    refinance: '市場金利で借り換え',
    repay: '一括完済',
  },
  values: {
    yearsShort: (n) => `${n} 年`,
    shareAbout: (pct) => `評価額の約 ${pct}`,
    sharePart: '評価額の一部',
    aroundAge: (age) => `（${age} 歳前後）`,
    inAboutYears: (n) => `（約 ${n} 年後）`,
    maySellFor: (share, when) => `プランが改善する場合に限り、売却して${share}${when}を資金化できます。`,
    maySell: 'プランが改善する場合に限り、売却できます。',
    structuredFact: '構造化データ',
  },
  ask: { talkThrough: 'MyCIO と詳しく話す' },
  review: {
    openInputCheckFailed: '未入力項目のチェックを完了できませんでした。下のレビュー状況をご確認ください。',
    finishedLoading: 'レビューが完了しました。結果を読み込んでいます…',
    draftReady: '暫定レビューの準備ができました。完了した分析、計算できなかった部分、それを埋めるための質問を下に示しています。',
    finishedNotLoaded: 'レビューは完了しましたが、結果をまだ読み込めていません。再試行しています（計算はやり直しません）。',
    fetchFailed: 'レビューは完了しましたが、現在取得できません。結果は保存されています。少し待ってから再度お試しください。再計算は不要です。',
    notComputed: (list) => `今回の実行で計算されなかった項目：${list}。`,
    changesNotSaved: (_n, f) => `${f} 件の変更が保存されませんでした。「お客様の情報」をご確認ください。`,
  },
  facts: {
    chooseOption: '選択肢を選んでください。',
    couldNotSave: 'その値を保存できませんでした。プランは変更されていません。',
    couldNotUndo: 'その変更を元に戻せませんでした。',
    choosePlaceholder: '選択してください…',
    confirmedBadge: '確認済み — 変更はチャットでご依頼ください',
    confirmedTitle: (was) => `この項目は以前 ${was} として確認済みです。置き換えるには、チャットで MyCIO にお伝えください。`,
  },
  assumptionsToConfirm: { anInput: 'ある入力項目', assumedSuffix: (v) => ` — 仮定値：${v}` },
  editor: {
    enterNumber: '数値を入力してください。',
    enterNumberFor: (l) => `「${l}」に数値を入力してください。`,
    enterPercentFor: (l) => `「${l}」にパーセンテージを入力してください。`,
    chooseYesNoFor: (l) => `「${l}」で「はい」または「いいえ」を選んでください。`,
    semicolonBetween: (l) => `「${l}」の各項目は「;」で区切ってください。`,
    useNumbersFor: (l) => `「${l}」には数値を使ってください。`,
    wholeNumbersFor: (l) => `「${l}」には整数を使い、「;」で区切ってください。`,
    commaSeparated: 'カンマ区切り',
  },
  freshnessUnknown: '保存された計算に最新の口座変更が反映されているか確認できませんでした。これらの数値に基づいて判断する前に、ページを再読み込みして口座をご確認ください。',
  goals: { goalN: (n) => `目標 ${n}`, whatMoves: 'これを動かす要因 →', earliest: (y) => `最短 ≈ ${y}`, rank: '順位' },
  issues: {
    mainConstraint: 'このプランの主な制約',
    riskToReview: '確認すべきリスク',
    missingInformation: '不足している情報',
    whyItMatters: (t) => `重要な理由：${t}`,
    seeEvidence: '根拠を見る ↓',
    noneFound: '完了した分析では、このプランに現在ある情報からは重要な課題は見つかりませんでした。',
    notAvailable: '課題の分析はまだ利用できません。このセクションが空であることを、課題がないという結果と受け取らないでください。',
    discussQuestion: 'このプランで見つかった課題を確認して、それぞれの根拠を説明してください。また、私の状況について不足している情報が、優先度や結論を変える可能性があるか質問してください。',
    discussLabel: 'これらの課題について話す',
  },
  policies: {
    optionTested: '検証した選択肢',
    computedEffect: (v) => `試算された効果：${v}`,
    tradeoff: (v) => `トレードオフ：${v}`,
    backToAction: 'アクションに戻る ↑',
    tradeoffsHeading: '検証したケースのトレードオフ',
  },
  transfer: {
    toHeirs: (m) => `相続人へ +${m}`,
    fundedPoints: (label, pts) => `${label} ${pts} ポイント`,
    noChange: ' · 目標の資金確保に変化なし',
  },
  tax: {
    rulesIntro: 'この計算に紐づくルールのみを表示しています。各項目には記録された出典情報を示し、日付や適用範囲の情報が欠けている場合はその旨を明示しています。',
    title: 'このプランで用いた税務上の取り扱い',
    countryBookPrimary: '予測では、該当する国の税制を年ごとに適用しました。公式または一次資料として明示的に記録された出典を下に示します。',
    countryBookNoPrimary: '予測では、該当する国の税制を年ごとに適用しました。この保存済みの結果にはまだ一次資料へのリンクが添付されていないため、税務に関わる判断は実行前にご確認ください。',
    simplified: 'この計算では簡略化した税務処理を用いています。税務に関わる比較に依拠する前に、下の制約事項をご確認ください。',
    askQuestion: 'このプランにどの税法と国ごとの階層が適用されたか、どれが近似または除外されたか、私の税務状況のどの事実が結果を変えうるかを説明してください。この計算に実際に添付された一次資料のみを使い、不足している出典を挙げてください。',
    askLabel: '税務上の取り扱いについて質問する',
  },
  inputs: { title: '結果を変えうる入力項目', infoToConfirm: '確認が必要な情報' },
  gated: {
    countChoices: (_n, f) => `お客様のために試算した ${f} 件の選択肢`,
    mappedYears: (_n, f) => `今後 ${f} 年間を試算`,
    extraChecks: (_n, f) => `プランを追加で ${f} 通りの方法で検証`,
    placeholderRows: ['例示の行', 'サンプルの数値', '例示の選択肢', 'プレースホルダー'],
  },
}

export default ja
